import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const LANGUAGES = {
  python: {
    label: 'Python',
    syntax: 'python',
    ext: 'py',
  },
  shell: {
    label: 'Shell',
    syntax: 'bash',
    ext: 'sh',
  },
};

const SCRIPTS = [
  {
    id: 'upload_universe_parts',
    title: 'Upload a Universe',
    basename: 'upload_universe_parts',
  },
  {
    id: 'upload_universe_with_properties',
    title: 'Upload a Universe with Properties',
    basename: 'upload_universe_with_properties',
  },
  {
    id: 'download_email_list',
    title: 'Download Email List',
    basename: 'download_email_list',
  },
  {
    id: 'download_signals',
    title: 'Download Signals',
    basename: 'download_signals',
  },
  {
    id: 'download_segment',
    title: 'Download Segment',
    basename: 'download_segment',
  },
  {
    id: 'get_dashboard',
    title: 'Get Dashboard',
    basename: 'get_dashboard',
  },
];

function samplePath(basename, ext) {
  return `/code/${basename}.${ext}`;
}

/**
 * API page Code tab: sample scripts with language toggle and copy.
 * Scripts are served from public/code/ (mirrors scripts/*.py and *.sh).
 */
function ApiCodeContent() {
  const [language, setLanguage] = useState('python');
  const [codeByPath, setCodeByPath] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedById, setExpandedById] = useState({});

  useEffect(() => {
    const base = process.env.PUBLIC_URL || '';
    let cancelled = false;
    const paths = SCRIPTS.flatMap((script) =>
      Object.values(LANGUAGES).map((lang) => samplePath(script.basename, lang.ext))
    );

    Promise.all(
      paths.map(async (path) => {
        const res = await fetch(`${base}${path}`);
        if (!res.ok) throw new Error(`Failed to load ${path}`);
        const text = await res.text();
        return [path, text];
      })
    )
      .then((entries) => {
        if (cancelled) return;
        setCodeByPath(Object.fromEntries(entries));
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load code samples');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleExpanded = (id) => {
    setExpandedById((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyScript = (path) => {
    const code = codeByPath[path];
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {error}
      </div>
    );
  }

  const lang = LANGUAGES[language];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-6" role="radiogroup" aria-label="Language">
        {Object.entries(LANGUAGES).map(([key, sample]) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="codeLanguage"
              value={key}
              checked={language === key}
              onChange={() => setLanguage(key)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
            />
            <span className="text-sm font-medium text-gray-700">{sample.label}</span>
          </label>
        ))}
      </div>

      {SCRIPTS.map((script) => {
        const path = samplePath(script.basename, lang.ext);
        const filename = `${script.basename}.${lang.ext}`;
        const code = codeByPath[path] || '';
        const expanded = Boolean(expandedById[script.id]);

        return (
          <div key={script.id} className="overflow-hidden rounded-lg border border-gray-200">
            <div className="flex items-center justify-between bg-gray-50 px-2 py-1.5">
              <button
                type="button"
                onClick={() => toggleExpanded(script.id)}
                className="flex min-w-0 items-center gap-1 rounded p-0.5 text-sm font-medium text-gray-800 hover:bg-gray-200"
                aria-expanded={expanded}
              >
                {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className="truncate">
                  {script.title}
                  <span className="ml-2 font-normal text-gray-500">{filename}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => copyScript(path)}
                disabled={loading || !code}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-40"
                aria-label={`Copy ${filename}`}
                title="Copy code"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            {expanded &&
              (loading ? (
                <pre className="min-h-[12rem] bg-white p-4 pr-12 font-mono text-sm text-gray-700">
                  Loading…
                </pre>
              ) : (
                <SyntaxHighlighter
                  language={lang.syntax}
                  style={oneLight}
                  customStyle={{
                    margin: 0,
                    borderRadius: 0,
                    backgroundColor: '#ffffff',
                    padding: '1rem 3rem 1rem 1rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.625,
                    minHeight: '12rem',
                  }}
                  codeTagProps={{
                    style: {
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    },
                  }}
                >
                  {code}
                </SyntaxHighlighter>
              ))}
          </div>
        );
      })}
    </div>
  );
}

export default ApiCodeContent;
